// ============================================================
// AiBriefingPanel.tsx — (Big-audit batch 4, M8) Admin → Security: the firm's
// "AI daily briefing" setting. A read-first card with a pencil (the house
// pattern): it says what's on and exactly what is sent; the draft saves with
// Save. Off by default for a new firm. Live: another admin's change arrives on
// the firm's 'settings' topic. The server enforces admin-only.
// ============================================================
import React, { useCallback, useEffect, useState } from 'react'
import { ErrorNote } from '../shared/ActionBox'
import { UnsavedGuard } from '../../hooks/useUnsavedChangesGuard'
import { SettingsCard, SettingsSaved, SettingsSwitch, SettingsToggleRow, S_NOTE } from './settingsUi'
import { EditFooter, EditPencil } from '../settlement/settlementUi'
import { errorText } from '../billing/billingUi'
import { getAiDailyBriefingApi, setAiDailyBriefingApi } from '../../api/casevoApi'
import { useFirmChange } from '../../utils/liveEvents'

export const AI_BRIEFING_NOTE = 'When this is on, Casevo sends titles, times and case names to Anthropic to write each person’s briefing. It never sends note text, client contact details or NI numbers. Anthropic processes this in the US and doesn’t use it for training.'

const AiBriefingPanel: React.FC = () => {
  const [value, setValue] = useState<boolean | null>(null)
  const [draft, setDraft] = useState<boolean | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const load = useCallback(() => {
    getAiDailyBriefingApi().then(setValue).catch((e: unknown) => setLoadErr(errorText(e, 'Couldn’t load this setting.')))
  }, [])
  useEffect(load, [load])
  useFirmChange(['settings'], load)

  // Keyboard focus never drops to the page: into the switch, back to the pencil.
  const toSwitch = (): void => { window.setTimeout(() => document.querySelector<HTMLElement>('#ai-briefing [role="switch"]')?.focus(), 0) }
  const toPencil = (): void => { window.setTimeout(() => document.querySelector<HTMLElement>('#ai-briefing [data-edit-pencil]')?.focus(), 0) }
  const save = async (): Promise<void> => {
    if (draft === null) return
    setSaving(true); setSaveErr(null)
    try {
      setValue(await setAiDailyBriefingApi(draft))
      setDraft(null); setSaved(true); toPencil()
    } catch (e) {
      setSaveErr(errorText(e, 'Couldn’t save this setting.'))
    } finally { setSaving(false) }
  }
  if (loadErr) return <ErrorNote>{loadErr}</ErrorNote>
  const editing = draft !== null
  return (
    <>
      <UnsavedGuard dirty={editing && draft !== value && !saving} />
      <SettingsCard id="ai-briefing" icon="list" title="AI daily briefing" sub="A short summary of each person’s day on their dashboard and calendar."
        editing={editing}
        right={editing || value === null ? null : <>{saved && <SettingsSaved />}<EditPencil title="AI daily briefing" onClick={() => { setSaved(false); setDraft(value); toSwitch() }} /></>}
        footer={editing ? <EditFooter blocked={false} saving={saving} error={saveErr} onCancel={() => { setDraft(null); setSaveErr(null); toPencil() }} onSave={() => { void save() }} /> : undefined}>
        {editing ? (
          <SettingsToggleRow title="AI daily briefing" desc={AI_BRIEFING_NOTE}
            control={<SettingsSwitch checked={!!draft} onChange={() => setDraft(!draft)} label="AI daily briefing" disabled={saving} />} />
        ) : (
          <dl className="m-0" data-read-view="ai-briefing">
            <dt className="text-[13px] text-casevo-text-soft">Status</dt>
            <dd className="m-0 mt-[2px] text-[14px] font-medium text-casevo-forest">{value === null ? 'Loading…' : value ? 'On' : 'Off'}</dd>
            <p className={`${S_NOTE} mt-[10px] mb-0 leading-[1.5]`}>{AI_BRIEFING_NOTE}</p>
          </dl>
        )}
      </SettingsCard>
    </>
  )
}

export default AiBriefingPanel
